/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import CareerCertificateItem from 'bundles/unified-description-page-common/components/modules/syllabus/CareerCertificateItem';
import OriginalCourseItemContent from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemContent';
import CourseItemContentRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemContentRedesign';
import OriginalCourseItemSubtitle from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemSubtitle';
import CourseItemSubtitleRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemSubtitleRedesign';
import CourseItemThumbnail from 'bundles/unified-description-page-common/components/modules/syllabus/CourseItemThumbnail';
import EligibleDegreesItem from 'bundles/unified-description-page-common/components/modules/syllabus/EligibleDegreesItem';
import OriginalSyllabusItem from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItem';
import SyllabusItemRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemRedesign';
import OriginalSyllabusItemTitle from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitle';
import SyllabusItemTitleRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitleRedesign';
import OriginalSyllabusModule from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusModule';
import SyllabusModuleRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusModuleRedesign';
import OriginalInstructorAndPartnerInfo from 'bundles/unified-description-page-common/components/shared/InstructorAndPartnerInfo';
import InstructorAndPartnerInfoRedesign from 'bundles/unified-description-page-common/components/shared/InstructorAndPartnerInfoRedesign';
import TogglableDescription from 'bundles/unified-description-page-common/components/shared/TogglableDescription';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import type { EnterpriseSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseSpecializationPageQuery';
import {
  getNumberOfInstructorsToShow,
  getNumberOfInstructorsToShowRedesign,
} from 'bundles/unified-description-page-common/utils/getNumberOfInstructorsToShow';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getSpecializationDescription from 'bundles/unified-description-page-common/utils/getSpecializationDescription';
import { getSpecializationSyllabusTitle } from 'bundles/unified-description-page-common/utils/syllabus';

import _t from 'i18n!nls/xdp';

/**
 * Props for the SpecializationSyllabusModuleRenderer component
 * @typedef {Object} Props
 * @property {EnterpriseSpecializationPageFieldsFragment | ConsumerSpecializationPageFieldsFragment} pageData - The specialization page data containing course information, instructors, partners, etc.
 * @property {Object} [config] - Optional configuration object
 * @property {boolean} [config.disableCta] - Whether to disable call-to-action buttons
 */
type Props = {
  pageData: EnterpriseSpecializationPageFieldsFragment | ConsumerSpecializationPageFieldsFragment;
  config?: {
    disableCta?: boolean;
    disableDegrees?: boolean;
    disableAceCta?: boolean;
  };
};

/**
 * SpecializationSyllabusModuleRenderer is a component that renders the syllabus section of a specialization or professional certificate page.
 * It displays a list of courses that are part of the syllabus, along with their details such as duration,
 * learning objectives, and skills. It also shows instructor and partner information in the sidebar.
 *
 * @component
 *
 * Required Context:
 * - PageInfoContext: Provides getCourseLink function for generating course URLs
 *
 * Features:
 * - Displays a list of courses with their details (name, duration, skills, learning objectives)
 * - Shows instructor and partner information in the sidebar
 * - Supports both enterprise and consumer specialization pages
 * - Includes career certificate and eligible degrees information
 * - Provides toggleable course descriptions
 * - Supports images for courses within the syllabus
 *
 * Edge Cases:
 * - Returns null if no courses are present in pageData
 * - Handles missing course images gracefully by not rendering thumbnail
 * - Supports both fixed and flexible session course types
 * - Gracefully handles missing optional fields (ratings, skills, etc.)
 *
 * Integration:
 * - Uses PageInfoContext for consistent URL generation
 *
 * @param {Props} props - Component props
 * @returns {React.ReactElement | null} The rendered component or null if no courses are available
 *
 * @throws {Error} Throws if used outside of PageInfoContext
 */
const SyllabusModuleRenderer = ({ pageData, config }: Props) => {
  const { getCourseLink } = usePageInfo();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;

  if (!pageData.courses || !pageData.courses.length) {
    return null;
  }

  const descriptionWithProjectOverview = getSpecializationDescription(
    pageData.cmlDescription,
    pageData.projectsOverview
  );
  const projectOverview = pageData.projectsOverview;
  const cmlDescription = enableXDPRedesignDescriptionRework ? projectOverview : descriptionWithProjectOverview;

  const SyllabusModule = enableXDPRedesign ? SyllabusModuleRedesign : OriginalSyllabusModule;
  const SyllabusItem = enableXDPRedesign ? SyllabusItemRedesign : OriginalSyllabusItem;
  const SyllabusItemTitle = enableXDPRedesign ? SyllabusItemTitleRedesign : OriginalSyllabusItemTitle;
  const CourseItemSubtitle = enableXDPRedesign ? CourseItemSubtitleRedesign : OriginalCourseItemSubtitle;
  const CourseItemContent = enableXDPRedesign ? CourseItemContentRedesign : OriginalCourseItemContent;
  const InstructorAndPartnerInfo = enableXDPRedesign
    ? InstructorAndPartnerInfoRedesign
    : OriginalInstructorAndPartnerInfo;
  const getNumberOfInstructorsToShowFunction = enableXDPRedesign
    ? getNumberOfInstructorsToShowRedesign
    : getNumberOfInstructorsToShow;

  return (
    <SyllabusModule
      anchorId={SPECIALIZATION_NAV_ITEMS.COURSES}
      title={getSpecializationSyllabusTitle(pageData.productVariant, pageData.courses.length, pageData.slug)}
      description={
        pageData.cmlLearningObjectives && (
          <TogglableDescription
            description={pageData.description}
            cmlDescription={cmlDescription}
            trackingName="syllabus_description"
            trackingData={{
              accordion: {
                name: 'description_page_syllabus_overview',
                header: 'Description page syllabus overview read more',
                body: '',
              },
            }}
          />
        )
      }
      slug={pageData.slug}
      syllabusItems={pageData.courses.map((course, index) => {
        /* eslint-disable no-restricted-syntax */
        const toggleButtonLabel = _t('Course details');
        const toggleButtonAriaLabel = _t('Course details for #{courseName}', { courseName: course.name });
        const linkA11yLabel = `${_t('Course #{courseIndex}:', { courseIndex: index + 1 })} ${course.name}`;
        /* eslint-enable no-restricted-syntax */
        const courseRating = enableXDPRedesign ? course.ratings?.averageFiveStarRating : undefined;

        return (
          <SyllabusItem
            toggleButtonLabel={toggleButtonLabel}
            toggleButtonAriaLabel={toggleButtonAriaLabel}
            key={course.id}
            itemIndex={index + 1}
            multiActionHeader
            trackingName="syllabus_course"
            thumbnail={
              course.photoUrl ? (
                <CourseItemThumbnail thumbnailUrl={course.photoUrl} thumbnailAltText={course.name} />
              ) : undefined
            }
          >
            <SyllabusItemTitle
              title={course.name}
              slug={pageData.slug}
              link={getCourseLink({ courseSlug: course.slug, specializationSlug: pageData.slug })}
              linkA11yLabel={linkA11yLabel}
            />
            <CourseItemSubtitle
              courseIndex={index + 1}
              totalContentDuration={course.totalContentDuration}
              rating={courseRating}
              ratingCount={course.ratings?.ratingCount}
              link={getCourseLink({ courseSlug: course.slug, specializationSlug: pageData.slug })}
            />
            <CourseItemContent
              s12nSlug={pageData.slug}
              courseSlug={course.slug}
              skills={course.skills}
              learningObjectives={course.cmlLearningObjectives}
              description={course.description}
              fixedSession={'fixedSession' in course ? course?.fixedSession : undefined}
              link={getCourseLink({ courseSlug: course.slug, specializationSlug: pageData.slug })}
            />
          </SyllabusItem>
        );
      })}
      sidebar={
        <InstructorAndPartnerInfo
          instructors={pageData.instructors}
          numberOfInstructorsToShow={getNumberOfInstructorsToShowFunction(
            pageData.instructors?.length,
            pageData.courses?.length
          )}
          partners={pageData.partners}
          industryPartners={pageData.companyLogos}
          trackingLocation="syllabus"
          disableCTA={config?.disableCta}
        />
      }
      footer={
        <>
          <CareerCertificateItem />
          {!config?.disableDegrees && (
            <EligibleDegreesItem
              degrees={pageData.degrees}
              partners={pageData.partners}
              slug={pageData.slug}
              productTags={pageData.productTags}
              productVariant={pageData.productVariant}
              disableCta={config?.disableCta}
              disableAceCta={config?.disableAceCta}
            />
          )}
        </>
      }
      addExtraDesktopPadding
    />
  );
};

export default SyllabusModuleRenderer;
